import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  TrendingUp, 
  AlertCircle, 
  Target, 
  Users,
  ArrowRight
} from 'lucide-react';

const stats = [
  { value: "1 in 5", label: "Adults experience mental health challenges annually" },
  { value: "60%", label: "Of those with mental illness don't receive treatment" },
  { value: "11 years", label: "Average delay between symptom onset and treatment" },
];

const challenges = [
  {
    icon: AlertCircle,
    title: "Fragmented Systems",
    description: "Healthcare systems are often complex and disconnected, making it difficult for individuals to find and access the services they need."
  },
  {
    icon: Target,
    title: "Limited Awareness",
    description: "Many people don't know what behavioral health resources are available to them or how to access these services."
  },
  {
    icon: TrendingUp,
    title: "Navigation Complexity",
    description: "The process of navigating healthcare pathways can be overwhelming, causing many to delay or avoid seeking help."
  },
  {
    icon: Users,
    title: "Support Network Gaps",
    description: "Communities often lack strong support networks that can help individuals connect with appropriate behavioral health services."
  },
];

const WhyThisMatters = () => {
  return (
    <div className="min-h-screen" data-testid="why-this-matters-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              Why This Matters
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Behavioral health challenges affect millions of individuals and families across the United States. Understanding the scope of this challenge is essential to addressing it effectively.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-brand-teal to-brand-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-white/80 text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
              The Challenge We Address
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Access to care is often complicated by fragmented systems, limited awareness of available resources, and the complexity of navigating healthcare pathways. Many individuals delay seeking help because they do not know where to begin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => {
              const Icon = challenge.icon;
              return (
                <Card 
                  key={index} 
                  className="bg-brand-neutral p-8 rounded-2xl border-none"
                  data-testid={`challenge-card-${index}`}
                >
                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon size={28} className="text-brand-orange" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-brand-blue mb-3">
                      {challenge.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {challenge.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-20 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-blue mb-6">
                How We Make a Difference
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Behavioral Health Navigation & Workforce Agency addresses these challenges by helping individuals and families understand available services and connect with appropriate support resources. By simplifying the navigation process, we contribute to improved access to behavioral health services and stronger community support systems.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our work focuses on bridging the gap between those who need behavioral health support and the resources available to help them. We believe that no one should struggle to find help simply because the system is too complex to navigate.
              </p>
              <Link to="/what-we-do">
                <Button 
                  className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-6 py-3"
                  data-testid="learn-more-btn"
                >
                  Learn About Our Services
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="font-heading text-xl font-bold text-brand-blue mb-6">
                Our Approach
              </h3>
              <ul className="space-y-4">
                {[
                  "Simplify the path to behavioral health resources",
                  "Provide guidance through complex healthcare systems",
                  "Connect individuals with appropriate services",
                  "Strengthen community support networks",
                  "Empower the behavioral health workforce"
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-brand-teal/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-brand-teal text-sm font-bold">{index + 1}</span>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Be Part of the Solution
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Whether you need support, want to partner with us, or are interested in strengthening your community's behavioral health resources, we invite you to connect with us.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner-with-us">
              <Button 
                className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-8 py-6 text-base font-semibold"
                data-testid="cta-partner-btn"
              >
                Partner With Us
              </Button>
            </Link>
            <Link to="/request-support">
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-brand-blue rounded-full px-8 py-6 text-base font-semibold"
                data-testid="cta-support-btn"
              >
                Request Support
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyThisMatters;
